import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { toPng } from "html-to-image";
import { WORD_WHEEL_QUEST_GOOGLE_PLAY_URL } from "../../../config/nativeAppsVisibility";
import Logger from "../../../services/Logger";
import S3Service from "../../../services/S3Service";
import LetterWheel, {
    buildWheelTiles,
    lettersForWheel,
} from "../../../prototype/mobile-word-wheel/components/LetterWheel";
import {
    buildWordClueNumbers,
    normalizeWord,
    parseFilledCoordinates,
    parseWordPositions,
    puzzleCellKeys,
} from "../../../prototype/mobile-word-wheel/lib/gridReveal";
import { wwColors } from "../../../prototype/mobile-word-wheel/lib/theme";
import { PUBLISHED } from "../../pzl_wordwheel/board/AdminWordWheelBoardService";
import AdminWordWheelService from "../../pzl_wordwheel/tpl/AdminWordWheelService";
import BannedService from "../../setting/banned_words/BannedService";
import DictionaryApiService from "../../system/integrate/DictionaryApiService";
import InstagramService from "../../system/instagram/InstagramService";
import FacebookTestService from "../../system/word-wheel-social/FacebookTestService";
import { FACEBOOK_HASHTAGS } from "../../system/word-wheel-social/facebookPromoCopy";
import {
    blockedBannedKeys,
    buildGuessCaption,
    gradientCss,
    isPublishedToday,
    lookupShortMeaning,
    pickGuessQuestion,
    pickMaskWordSlots,
    matchBannedWord,
    parseWordsInUse,
    pickFocusWord,
    pickNextFocusWord,
    scoreByWordFromDetails,
    scoreByWordFromDictionary,
    mergeWordScores,
    nextWarmGradientSet,
    slotsFromMaskedCaption,
    WARM_GRADIENT_SETS,
} from "./guessWordUtils";
import SocialTabs from "./SocialTabs";
import WordWheelSocialDraftService from "./WordWheelSocialDraftService";

const CARD_W = 1200;
const CARD_H = 630;
const IG_CARD = 1080;
const CARD_FONT = '"Nunito", "Segoe UI", "Trebuchet MS", sans-serif';
const GRADIENT_STORAGE_KEY = "admin.ww.social.gradient";

function readLastGradientId() {
    try {
        return sessionStorage.getItem(GRADIENT_STORAGE_KEY) || "";
    } catch {
        return "";
    }
}

function writeLastGradientId(id) {
    try {
        if (id) sessionStorage.setItem(GRADIENT_STORAGE_KEY, id);
    } catch {
        /* ignore */
    }
}

function cycleGradientSet(currentId) {
    const next = nextWarmGradientSet(currentId || readLastGradientId());
    if (next?.id) writeLastGradientId(next.id);
    return next;
}

function dataUrlToPngFile(dataUrl, fileName) {
    const comma = String(dataUrl || "").indexOf(",");
    const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: "image/png" });
}

function wordsFromPuzzle(puzzle) {
    return parseWordsInUse(puzzle?.wordsInUse);
}

function hasPuzzleGrid(puzzle) {
    return puzzleCellKeys(parseWordPositions(puzzle?.filledCoordinates)).size > 0;
}

function letterAtCoordIndex(coord, i) {
    const w = normalizeWord(coord.word || coord.text);
    const positions = coord.positions || [];
    const fromCell = String(positions[i]?.value || positions[i]?.letter || "").trim().toUpperCase();
    if (fromCell) return fromCell;
    if (!w) return "";
    const letterIndex = coord.reversed ? w.length - 1 - i : i;
    if (letterIndex < 0 || letterIndex >= w.length) return "";
    return w.charAt(letterIndex);
}

function fillCoordLetters(map, coord) {
    (coord.positions || []).forEach((p, i) => {
        const ch = letterAtCoordIndex(coord, i);
        if (ch) map.set(`${p.row},${p.col}`, ch);
    });
}

function focusCoordForWord(coords, word) {
    const w = normalizeWord(word);
    return coords.find((coord) => normalizeWord(coord.word || coord.text) === w) || null;
}

function focusCellKeys(coord) {
    const keys = new Set();
    (coord?.positions || []).forEach((p) => keys.add(`${p.row},${p.col}`));
    return keys;
}

/** Words that share a cell with the guess word (not the guess word itself). */
function crossingCoords(coords, focus) {
    const focusKeys = focusCellKeys(focus);
    const focusWord = normalizeWord(focus?.word || focus?.text);
    return coords.filter((coord) => {
        const cw = normalizeWord(coord.word || coord.text);
        if (!cw || cw === focusWord) return false;
        return (coord.positions || []).some((p) => focusKeys.has(`${p.row},${p.col}`));
    });
}

/** Top blanks: only letters that other (crossing) words expose. */
function crossingRevealSlots(filledCoordinates, word) {
    const w = normalizeWord(word);
    const slots = Array.from({ length: w.length }, () => "");
    if (!w) return slots;
    const coords = parseFilledCoordinates(filledCoordinates);
    const focus = focusCoordForWord(coords, w);
    if (!focus) return slots;
    const focusKeys = focusCellKeys(focus);
    const exposed = new Set();
    crossingCoords(coords, focus).forEach((coord) => {
        (coord.positions || []).forEach((p) => {
            const key = `${p.row},${p.col}`;
            if (focusKeys.has(key)) exposed.add(key);
        });
    });
    (focus.positions || []).forEach((p, i) => {
        const letterIndex = focus.reversed ? w.length - 1 - i : i;
        if (letterIndex < 0 || letterIndex >= w.length) return;
        if (!exposed.has(`${p.row},${p.col}`)) return;
        slots[letterIndex] = w.charAt(letterIndex);
    });
    return slots;
}

/** Fill crossing words completely; guess-word cells only where those words overlap. */
function revealedGridLetters(filledCoordinates, word) {
    const map = new Map();
    const coords = parseFilledCoordinates(filledCoordinates);
    const focus = focusCoordForWord(coords, word);
    if (!focus) return map;
    crossingCoords(coords, focus).forEach((coord) => fillCoordLetters(map, coord));
    return map;
}

function GuessLetterGrid({ puzzle, size = 280, focusWord = "" }) {
    const wordPositions = parseWordPositions(puzzle?.filledCoordinates);
    const puzzleCells = puzzleCellKeys(wordPositions);
    const { cellToNumbers } = buildWordClueNumbers(puzzle?.filledCoordinates);
    const revealed = revealedGridLetters(puzzle?.filledCoordinates, focusWord);
    let minR = Number.POSITIVE_INFINITY;
    let minC = Number.POSITIVE_INFINITY;
    let maxR = -1;
    let maxC = -1;
    puzzleCells.forEach((key) => {
        const [rowRaw, colRaw] = String(key).split(",");
        const row = Number(rowRaw);
        const col = Number(colRaw);
        if (!Number.isFinite(row) || !Number.isFinite(col)) return;
        minR = Math.min(minR, row);
        minC = Math.min(minC, col);
        maxR = Math.max(maxR, row);
        maxC = Math.max(maxC, col);
    });
    if (maxR < 0) {
        return null;
    }
    const n = Math.max(maxR - minR + 1, maxC - minC + 1);
    return (
        <Box
            sx={{
                width: size,
                height: size,
                display: "grid",
                gridTemplateColumns: `repeat(${n}, 1fr)`,
                gridTemplateRows: `repeat(${n}, 1fr)`,
                gap: "4px",
            }}
        >
            {Array.from({ length: n * n }, (_, i) => {
                const row = minR + Math.floor(i / n);
                const col = minC + (i % n);
                const cellKey = `${row},${col}`;
                const isPuzzle = puzzleCells.has(cellKey);
                const numberLabel = (cellToNumbers.get(cellKey) || []).join("/");
                const letter = revealed.get(cellKey) || "";
                return (
                    <Box
                        key={cellKey}
                        sx={{
                            position: "relative",
                            borderRadius: "6px",
                            bgcolor: isPuzzle ? "rgba(255,255,255,0.95)" : "transparent",
                            border: isPuzzle ? "1.5px solid #0d9488" : "1px solid transparent",
                            minWidth: 0,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#0f172a",
                            fontWeight: 800,
                            fontFamily: CARD_FONT,
                            fontSize: Math.max(14, Math.round(size / n / 2.1)),
                            lineHeight: 1,
                        }}
                    >
                        {numberLabel && isPuzzle ? (
                            <Box
                                component="span"
                                sx={{
                                    position: "absolute",
                                    top: 1,
                                    left: 2,
                                    fontSize: Math.max(
                                        8,
                                        Math.round(size / n / (numberLabel.includes("/") ? 5.2 : 4)),
                                    ),
                                    fontWeight: 800,
                                    color: "#064e3b",
                                    lineHeight: 1,
                                }}
                            >
                                {numberLabel}
                            </Box>
                        ) : null}
                        {letter}
                    </Box>
                );
            })}
        </Box>
    );
}

function LetterTileGrid({ tiles, size = 280 }) {
    const letters = (tiles || [])
        .map((tile) => (typeof tile === "string" ? tile : tile?.letter) || "")
        .map((ch) => String(ch).trim().toUpperCase())
        .filter(Boolean);
    if (letters.length === 0) return null;
    const cols = Math.ceil(Math.sqrt(letters.length));
    const tile = Math.max(36, Math.min(72, Math.floor((size - 12) / cols - 8)));
    return (
        <Box
            sx={{
                width: size,
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, ${tile}px)`,
                justifyContent: "center",
                gap: "10px",
            }}
        >
            {letters.map((ch, i) => (
                <Box
                    key={`${ch}-${i}`}
                    sx={{
                        width: tile,
                        height: tile,
                        borderRadius: 2,
                        bgcolor: "#fff",
                        color: "#0f172a",
                        border: "2px solid #0d9488",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: Math.max(18, Math.round(tile / 2)),
                        fontFamily: CARD_FONT,
                    }}
                >
                    {ch}
                </Box>
            ))}
        </Box>
    );
}

function GuessCard({
    captureRef,
    width,
    height,
    cardGradient,
    headline,
    clue,
    slots = [],
    wheelTiles,
    wheelSize = 280,
    letterW = 72,
    letterH = 88,
    showGrid = false,
    puzzle = null,
    focusWord = "",
}) {
    const displayClue = clue;
    const n = (slots || []).length;
    const landscapeGrid = showGrid && height < width;
    const landscapeCard = !showGrid && height < width;
    const maxRowW = width - (landscapeGrid ? 96 : landscapeCard ? 100 : 120);
    const gapPx = landscapeGrid ? 22 : landscapeCard ? 16 : 12;
    const boxTarget = landscapeGrid ? 136 : landscapeCard ? 112 : 100;
    const fittedBox =
        n > 0
            ? Math.min(boxTarget, Math.max(landscapeGrid ? 88 : 64, Math.floor((maxRowW - (n - 1) * gapPx) / n)))
            : boxTarget;
    const slotW = landscapeGrid || !showGrid ? fittedBox : letterW;
    const slotH = landscapeGrid
        ? Math.round(fittedBox * 1.24)
        : !showGrid
            ? Math.round(fittedBox * 1.22)
            : letterH;
    const letterFont = landscapeGrid
        ? Math.max(56, Math.round(fittedBox * 0.65))
        : Math.max(48, Math.round(slotW * 0.62));
    const blankFont = landscapeGrid
        ? Math.max(64, Math.round(fittedBox * 0.76))
        : Math.max(56, Math.round(slotW * 0.72));
    const displayWheelSize = landscapeCard
        ? Math.min(wheelSize, Math.round(height * 0.38))
        : wheelSize;
    return (
        <Box
            ref={captureRef}
            sx={{
                width,
                height,
                background: cardGradient,
                color: "#fff",
                fontFamily: CARD_FONT,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                px: landscapeGrid ? 5 : 6,
                py: landscapeGrid ? 3.5 : 4,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: landscapeGrid || landscapeCard ? "flex-start" : "center",
                    gap: landscapeCard ? 1 : 0,
                }}
            >
                <Typography
                    sx={{
                        fontFamily: CARD_FONT,
                        fontWeight: 800,
                        fontSize: showGrid
                            ? height >= width
                                ? 72
                                : 52
                            : height >= width
                              ? 48
                              : 40,
                        letterSpacing: 0.2,
                        lineHeight: 1.2,
                        textAlign: "center",
                        flexShrink: 0,
                        mb: landscapeGrid ? 0 : landscapeCard ? 1 : 1.5,
                        pt: landscapeGrid ? 0.5 : 0,
                        textShadow: "0 1px 10px rgba(0,0,0,0.35)",
                    }}
                >
                    {headline}
                </Typography>
                <Box
                    sx={{
                        flex: landscapeGrid ? 1 : "0 0 auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        minHeight: 0,
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={0}
                        sx={{
                            mb: displayClue ? (landscapeGrid ? 2 : 1) : 1,
                            gap: `${gapPx}px`,
                        }}
                    >
                    {(slots || []).map((ch, i) =>
                        ch ? (
                            <Box
                                key={`${ch}-${i}`}
                                sx={{
                                    width: slotW,
                                    height: slotH,
                                    borderRadius: landscapeGrid ? 2.5 : 2,
                                    bgcolor: "rgba(255,255,255,0.95)",
                                    color: "#0f172a",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: letterFont,
                                    fontWeight: 700,
                                    fontFamily: CARD_FONT,
                                    border: landscapeGrid ? "5px solid #5eead4" : "3px solid #5eead4",
                                }}
                            >
                                {ch}
                            </Box>
                        ) : (
                            <Box
                                key={`blank-${i}`}
                                sx={{
                                    width: slotW,
                                    height: slotH,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: blankFont,
                                    fontWeight: 700,
                                    fontFamily: CARD_FONT,
                                    lineHeight: 1,
                                    color: "#fff",
                                    textShadow: "0 1px 8px rgba(0,0,0,0.35)",
                                }}
                            >
                                _
                            </Box>
                        ),
                    )}
                </Stack>
                {/* Clue hidden on social posts for now.
                {displayClue ? (
                    <Typography
                        sx={{
                            fontFamily: CARD_FONT,
                            fontWeight: 600,
                            fontSize: landscapeGrid ? 28 : height >= width ? 22 : 20,
                            lineHeight: landscapeGrid ? 1.4 : 1.35,
                            textAlign: "center",
                            maxWidth: landscapeGrid ? "90%" : "90%",
                            px: landscapeGrid ? 2 : 0,
                            mb: landscapeGrid ? 1.5 : 1,
                            opacity: 0.95,
                            textShadow: "0 1px 8px rgba(0,0,0,0.35)",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            display: "-webkit-box",
                            WebkitLineClamp: landscapeGrid ? 5 : 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {displayClue}
                    </Typography>
                ) : null}
                */}
                </Box>
                {showGrid ? null : (
                    <Box sx={{ width: displayWheelSize, height: displayWheelSize, mt: landscapeCard ? 0.5 : 0 }}>
                        <LetterWheel
                            tiles={wheelTiles}
                            selectedIndices={[]}
                            onSelectionChange={() => {}}
                            colors={wwColors}
                            fill
                            backdrop="helm"
                        />
                    </Box>
                )}
                <Typography
                    sx={{
                        mt: showGrid ? 0 : landscapeCard ? 0.75 : 1,
                        mb: showGrid ? 0.5 : 0,
                        flexShrink: 0,
                        fontSize: height >= width ? 20 : 18,
                        fontWeight: 700,
                        fontFamily: CARD_FONT,
                    }}
                >
                    Word Wheel Quest
                </Typography>
            </Box>
        </Box>
    );
}

export default function WordWheelGuessSocialMain({ layout = "wheel" }) {
    const showGrid = layout === "grid";
    const captureRef = useRef(null);
    const igCaptureRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [puzzle, setPuzzle] = useState(null);
    const [sourceNote, setSourceNote] = useState("");
    const [focusWord, setFocusWord] = useState("");
    const [postingTo, setPostingTo] = useState(null);
    const [postResult, setPostResult] = useState(null);
    const [postError, setPostError] = useState(null);
    const [preparedDraft, setPreparedDraft] = useState(null);
    const [gradientSetId, setGradientSetId] = useState(() => cycleGradientSet().id);
    const [bannedList, setBannedList] = useState([]);
    const [bannedLoading, setBannedLoading] = useState(false);
    const [bannedError, setBannedError] = useState(null);
    const [wordScores, setWordScores] = useState(() => new Map());
    const [slots, setSlots] = useState([]);
    const [headline, setHeadline] = useState(() => pickGuessQuestion());

    const words = useMemo(() => wordsFromPuzzle(puzzle), [puzzle]);
    const wheelLetters = useMemo(
        () => lettersForWheel(words, puzzle?.lettersGrid, puzzle?.details),
        [puzzle, words],
    );
    const wheelTiles = useMemo(
        () => buildWheelTiles(wheelLetters, puzzle?.id || "guess-wheel"),
        [wheelLetters, puzzle?.id],
    );
    const answerMeaning = useMemo(
        () => lookupShortMeaning(puzzle?.displayClue, focusWord),
        [puzzle?.displayClue, focusWord],
    );
    const bannedKeys = useMemo(() => blockedBannedKeys(bannedList), [bannedList]);
    const bannedHit = useMemo(
        () => matchBannedWord(bannedList, focusWord),
        [bannedList, focusWord],
    );
    const gradientSet = useMemo(
        () => WARM_GRADIENT_SETS.find((s) => s.id === gradientSetId) || WARM_GRADIENT_SETS[0],
        [gradientSetId],
    );
    const cardGradient = gradientCss(gradientSet);
    const gridSlots = useMemo(
        () => crossingRevealSlots(puzzle?.filledCoordinates, focusWord),
        [puzzle?.filledCoordinates, focusWord],
    );
    const displaySlots = showGrid ? gridSlots : slots;
    const caption = useMemo(
        () =>
            buildGuessCaption({
                headline,
                word: focusWord,
                slots: displaySlots,
                letters: wheelLetters,
                hashtags: FACEBOOK_HASHTAGS,
                playStoreUrl: WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
                clue: answerMeaning,
                showGrid,
            }),
        [headline, focusWord, displaySlots, wheelLetters, answerMeaning, showGrid],
    );

    const loadPuzzle = useCallback(async () => {
        setLoading(true);
        setError(null);
        setPostResult(null);
        setPostError(null);
        try {
            let rows = [];
            await AdminWordWheelService.requestList(
                { page: 0, pageSize: 20 },
                [{ field: "publishedAt", sort: "desc" }],
                (next) => {
                    rows = Array.isArray(next) ? next : [];
                },
                () => {},
                "",
                PUBLISHED,
                "english",
            );
            if (rows.length === 0) {
                setPuzzle(null);
                setFocusWord("");
                setSlots([]);
                setPreparedDraft(null);
                setBannedList([]);
                setBannedError(null);
                setError("No published Word Wheel puzzles found.");
                return;
            }
            const todayRow = rows.find((r) => isPublishedToday(r.publishedAt));
            const chosen = todayRow || rows[0];
            const tpl = await AdminWordWheelService.requestTemplate(chosen.id);
            const loaded = tpl && tpl.id ? tpl : chosen;
            setPuzzle(loaded);
            let draft = null;
            if (!showGrid) {
                try {
                    draft = await WordWheelSocialDraftService.requestLatest();
                } catch {
                    draft = null;
                }
            }
            setPreparedDraft(draft);
            const language = String(loaded.language || "english").toLowerCase();
            let list = [];
            setBannedError(null);
            setBannedLoading(!showGrid);
            try {
                const raw = await BannedService.requestList(language);
                list = Array.isArray(raw) ? raw : [];
                setBannedList(list);
            } catch (err) {
                setBannedList([]);
                setBannedError(err?.message || "Failed to check banned list");
            } finally {
                setBannedLoading(false);
            }
            const puzzleWords = wordsFromPuzzle(loaded);
            let dictScores = new Map();
            try {
                const infoRows = await DictionaryApiService.getMultipleWordsInfo(puzzleWords, language);
                dictScores = scoreByWordFromDictionary(infoRows);
            } catch {
                dictScores = new Map();
            }
            const scores = mergeWordScores(dictScores, scoreByWordFromDetails(loaded?.details));
            setWordScores(scores);
            const draftMatchesPuzzle =
                !showGrid
                && draft
                && loaded?.id
                && String(draft.puzzleId) === String(loaded.id)
                && typeof draft.word === "string"
                && draft.word.trim();
            const nextWord = draftMatchesPuzzle
                ? draft.word.trim().toUpperCase()
                : pickFocusWord(puzzleWords, Math.random, blockedBannedKeys(list), scores);
            setFocusWord(nextWord);
            setSlots(
                showGrid
                    ? crossingRevealSlots(loaded?.filledCoordinates, nextWord)
                    : draftMatchesPuzzle && draft.maskedWord
                        ? slotsFromMaskedCaption(draft.maskedWord, nextWord)
                        : pickMaskWordSlots(nextWord),
            );
            setHeadline(pickGuessQuestion());
            setSourceNote(
                todayRow
                    ? "Using today’s published puzzle."
                    : "No puzzle published today — using the most recently published one.",
            );
        } catch (e) {
            setPuzzle(null);
            setPreparedDraft(null);
            setFocusWord("");
            setSlots([]);
            setBannedList([]);
            setWordScores(new Map());
            setError(e?.message || "Failed to load a Word Wheel puzzle");
        } finally {
            setLoading(false);
        }
    }, [showGrid]);

    useEffect(() => {
        loadPuzzle();
    }, [loadPuzzle]);

    const shuffleWordAndPromo = () => {
        const nextWord = pickNextFocusWord(words, focusWord, bannedKeys, wordScores);
        setFocusWord(nextWord);
        setSlots(
            showGrid
                ? crossingRevealSlots(puzzle?.filledCoordinates, nextWord)
                : pickMaskWordSlots(nextWord),
        );
        setHeadline(pickGuessQuestion());
        setGradientSetId(cycleGradientSet(gradientSetId).id);
        setPostResult(null);
        setPostError(null);
    };

    const captureCardPng = async (node, width, height) => {
        if (!node) {
            throw new Error("Preview is not ready to capture.");
        }
        if (document.fonts?.ready) {
            await document.fonts.ready;
        }
        return toPng(node, {
            width,
            height,
            pixelRatio: 1,
            cacheBust: true,
            style: {
                transform: "none",
                width: `${width}px`,
                height: `${height}px`,
            },
            useCORS: true,
        });
    };

    const publishToFacebook = async () => {
        if (!puzzle || !focusWord) {
            setPostError("Preview is not ready to capture.");
            return;
        }
        if (bannedHit) {
            setPostError(`"${focusWord}" is banned (${bannedHit.category}).`);
            return;
        }
        setPostingTo("facebook");
        setPostError(null);
        setPostResult(null);
        try {
            const dataUrl = await captureCardPng(captureRef.current, CARD_W, CARD_H);
            const result = await FacebookTestService.postImage(dataUrl, caption, {
                published: true,
                fileName: `word-wheel-guess-${puzzle.id || "puzzle"}-fb.png`,
            });
            setPostResult({ platform: "facebook", ...result });
            const facebookPostId = result.postId || result.photoId;
            if (result.published && facebookPostId) {
                try {
                    await WordWheelSocialDraftService.markPosted({
                        id: preparedDraft?.id,
                        puzzleId: puzzle.id,
                        word: focusWord,
                        maskedWord: (slots || []).map((ch) => (ch ? String(ch).toUpperCase() : "_")).join(" "),
                        facebookPostId,
                    });
                } catch (markErr) {
                    Logger.warn("Word Wheel Facebook post saved, but homepage mark-posted failed:", markErr);
                }
            }
        } catch (e) {
            setPostError(e?.message || "Failed to post to Facebook");
        } finally {
            setPostingTo(null);
        }
    };

    const publishToInstagram = async () => {
        if (!puzzle || !focusWord) {
            setPostError("Preview is not ready to capture.");
            return;
        }
        if (bannedHit) {
            setPostError(`"${focusWord}" is banned (${bannedHit.category}).`);
            return;
        }
        setPostingTo("instagram");
        setPostError(null);
        setPostResult(null);
        try {
            const dataUrl = await captureCardPng(igCaptureRef.current, IG_CARD, IG_CARD);
            const fileName = `word-wheel-guess-${puzzle.id || "puzzle"}-${Date.now()}.png`;
            const file = dataUrlToPngFile(dataUrl, fileName);
            const uploaded = await S3Service.uploadToS3(file, fileName, "word-wheel-social");
            const imageUrl = uploaded?.url;
            if (!imageUrl || !String(imageUrl).startsWith("https://")) {
                throw new Error("Image upload did not return a public HTTPS URL.");
            }
            const result = await InstagramService.postImage(imageUrl, caption);
            setPostResult({ platform: "instagram", imageUrl, ...result });
        } catch (e) {
            setPostError(e?.message || "Failed to post to Instagram");
        } finally {
            setPostingTo(null);
        }
    };

    const previewMaxW = 720;
    const scale = Math.min(1, previewMaxW / CARD_W);
    const previewW = Math.round(CARD_W * scale);
    const previewH = Math.round(CARD_H * scale);

    return (
        <Box sx={{ p: 2, pb: 6, maxWidth: 960, mx: "auto" }}>
            <SocialTabs />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {showGrid ? "Word Wheel (Grid)" : "Word Wheel"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {showGrid
                    ? "Guess-the-word with blanks. The crossword grid is hidden. Use this page to test the 2nd scheduled post."
                    : "Guess-the-word with the letter wheel. Each publish rotates to a different background color."}
            </Typography>

            {sourceNote ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                    {sourceNote}
                </Typography>
            ) : null}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : null}

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            {!loading && !showGrid && preparedDraft?.imageUrl ? (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Prepared scheduler draft
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        {preparedDraft.status === "READY" || preparedDraft.status === "DRAFTED"
                            ? "This image and caption are what WORD_WHEEL_FACEBOOK_POST will publish next to Facebook and Instagram."
                            : `Status: ${preparedDraft.status || "unknown"}.`}
                    </Typography>
                    <Box
                        component="img"
                        src={preparedDraft.imageUrl}
                        alt="Prepared Word Wheel Facebook draft"
                        sx={{
                            display: "block",
                            width: "100%",
                            maxWidth: previewW,
                            height: "auto",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            mb: 1,
                        }}
                    />
                    {preparedDraft.caption ? (
                        <Box
                            component="pre"
                            sx={{
                                p: 2,
                                bgcolor: "action.hover",
                                borderRadius: 1,
                                whiteSpace: "pre-wrap",
                                fontFamily: "inherit",
                                fontSize: 14,
                            }}
                        >
                            {preparedDraft.caption}
                        </Box>
                    ) : null}
                </Box>
            ) : null}

            {!loading && puzzle ? (
                <>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                        <Button variant="outlined" onClick={shuffleWordAndPromo} disabled={Boolean(postingTo)}>
                            Shuffle word
                        </Button>
                        <Button variant="outlined" onClick={loadPuzzle} disabled={Boolean(postingTo)}>
                            Reload puzzle
                        </Button>
                        {!showGrid ? (
                            <>
                                <Button
                                    variant="contained"
                                    onClick={publishToFacebook}
                                    disabled={Boolean(postingTo) || !focusWord || Boolean(bannedHit) || bannedLoading}
                                >
                                    {postingTo === "facebook" ? "Publishing…" : "Publish to Facebook"}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={publishToInstagram}
                                    disabled={Boolean(postingTo) || !focusWord || Boolean(bannedHit) || bannedLoading}
                                >
                                    {postingTo === "instagram" ? "Publishing…" : "Publish to Instagram"}
                                </Button>
                            </>
                        ) : null}
                    </Stack>

                    {!showGrid && focusWord ? (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                    Answer: {focusWord}
                                </Box>
                                {answerMeaning ? ` — ${answerMeaning}` : ""}
                            </Typography>
                            {bannedLoading ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    Checking banned list…
                                </Typography>
                            ) : bannedError ? (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    Could not check banned list.
                                </Alert>
                            ) : bannedHit ? (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    Banned ({bannedHit.category}) — not valid.
                                </Alert>
                            ) : (
                                <Alert severity="success" sx={{ mt: 1 }}>
                                    Valid — not on the banned list.
                                </Alert>
                            )}
                        </Box>
                    ) : null}

                    {!showGrid ? (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Background
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                                {WARM_GRADIENT_SETS.map((set) => {
                                    const selected = set.id === gradientSet.id;
                                    return (
                                        <Box
                                            key={set.id}
                                            onClick={() => {
                                                setGradientSetId(set.id);
                                                writeLastGradientId(set.id);
                                            }}
                                            sx={{
                                                width: 72,
                                                cursor: "pointer",
                                                textAlign: "center",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: 36,
                                                    borderRadius: 1,
                                                    background: gradientCss(set),
                                                    border: selected ? "2px solid" : "1px solid",
                                                    borderColor: selected ? "primary.main" : "divider",
                                                    boxShadow: selected
                                                        ? "0 0 0 2px rgba(25,118,210,0.25)"
                                                        : "none",
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                                {set.name}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ) : null}

                    <Box
                        sx={{
                            width: previewW,
                            height: previewH,
                            mx: "auto",
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: 2,
                            boxShadow: "0 8px 28px rgba(15,23,42,0.18)",
                            border: "1px solid",
                            borderColor: "divider",
                            mb: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                transform: `scale(${scale})`,
                                transformOrigin: "top left",
                            }}
                        >
                            <GuessCard
                                captureRef={captureRef}
                                width={CARD_W}
                                height={CARD_H}
                                cardGradient={cardGradient}
                                headline={headline}
                                clue={answerMeaning}
                                slots={displaySlots}
                                wheelTiles={wheelTiles}
                                showGrid={showGrid}
                                puzzle={puzzle}
                                focusWord={focusWord}
                            />
                        </Box>
                    </Box>
                    {!showGrid ? (
                        <Box
                            aria-hidden
                            sx={{
                                position: "fixed",
                                left: 0,
                                top: 0,
                                zIndex: -1,
                                opacity: 0.01,
                                width: IG_CARD,
                                height: IG_CARD,
                                overflow: "hidden",
                                pointerEvents: "none",
                            }}
                        >
                            <GuessCard
                                captureRef={igCaptureRef}
                                width={IG_CARD}
                                height={IG_CARD}
                                cardGradient={cardGradient}
                                headline={headline}
                                clue={answerMeaning}
                                slots={displaySlots}
                                wheelTiles={wheelTiles}
                                wheelSize={420}
                                showGrid={showGrid}
                                puzzle={puzzle}
                                focusWord={focusWord}
                            />
                        </Box>
                    ) : null}

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
                        Caption
                    </Typography>
                    <Box
                        component="pre"
                        sx={{
                            p: 2,
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            whiteSpace: "pre-wrap",
                            fontFamily: "inherit",
                            fontSize: 14,
                        }}
                    >
                        {caption}
                    </Box>

                    {!showGrid && postError ? (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {postError}
                        </Alert>
                    ) : null}
                    {!showGrid && postResult ? (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            {postResult.platform === "instagram"
                                ? `Posted to Instagram${
                                    postResult.published?.id || postResult.id || postResult.creation_id
                                        ? ` (id ${postResult.published?.id || postResult.id || postResult.creation_id})`
                                        : ""
                                }. Check the Instagram feed.`
                                : `Posted to the Facebook Page${
                                    postResult.postId || postResult.id
                                        ? ` (id ${postResult.postId || postResult.id})`
                                        : ""
                                }. Check the Page timeline.`}
                        </Alert>
                    ) : null}
                </>
            ) : null}
        </Box>
    );
}
